import React, { useMemo } from "react";
import { Box, Group } from "@mantine/core";
import { MainSection as MainSectionConfig } from "@/config/layoutConfig";
import {
  DisplayContentAssignment,
  DisplayScheduleData,
  DisplayZone,
} from "@/utils/api";
import { ContentBlockArea } from "@/components/features/ContentBlockArea";

import { DisplayBlockSchedule } from "@/components/features/DisplayBlockSchedule";
import { BlockTransitionAnimationType } from "../../BlockTransitionAnimationSelector";
import { ProgressStyleValue } from "../../ProgressStyleSelector";

export interface MainSectionProps {
  /** Main section configuration from layout config */
  section: MainSectionConfig;

  /** Schedule data for events */
  scheduleData: DisplayScheduleData;

  /** Display zones from display configuration (new system) */
  displayZones?: DisplayZone[];

  /** Regular content block assignments (legacy system) */
  displayContentAssignments?: DisplayContentAssignment[];

  /** Emergency content block assignments (overrides zones) */
  emergencyContentAssignments: DisplayContentAssignment[];

  /** Room name to display in schedule */
  roomName?: string;

  /** Theme configuration */
  themeConfig?: any;

  /** Additional CSS class name for styling */
  className?: string;
}

export const MainSection: React.FC<MainSectionProps> = ({
  section,
  scheduleData,
  displayZones,
  emergencyContentAssignments,
  roomName,
  themeConfig,
  className,
}) => {
  // Emergency mode - override zones and show full-screen emergency content
  const hasEmergencyContent = emergencyContentAssignments.length > 0;

  // Create a map of displayZones by displayZoneId for quick lookup
  const displayZonesMap = useMemo(() => {
    const map = new Map<string, DisplayZone>();
    if (displayZones) {
      displayZones.forEach((zone) => {
        if (zone.displayZoneId) {
          map.set(zone.displayZoneId, zone);
        }
      });
    }
    return map;
  }, [displayZones]);

  // Determine if we're using the new zone-based system or legacy system
  const useZoneBasedSystem = displayZones && displayZones.length > 0;

  if (hasEmergencyContent) {
    return (
      <Box
        className={`main-section main-section--emergency ${className || ""}`}
        style={{
          width: "100%",
          height: "100%",
          overflow: "hidden",
        }}
      >
        <ContentBlockArea
          displayContentAssignments={emergencyContentAssignments}
          venue={scheduleData.venue}
          themeConfig={themeConfig}
          progressStyle="none"
        />
      </Box>
    );
  }

  // Normal mode - render zones based on configuration
  return (
    <Box
      className={`main-section ${className || ""}`}
      style={{
        width: "100%",
        height: "100%",
        overflow: "hidden",
      }}
      data-system={useZoneBasedSystem ? "zones" : "legacy"}
      data-zones-count={section.zones.length}
    >
      <Group
        gap={section.gap}
        wrap="nowrap"
        align="stretch"
        style={{
          height: "100%",
        }}
      >
        {section.zones.map((layoutZone) => {
          const zoneKey = layoutZone.id;

          // Try to match with displayZone (new system)
          const displayZone = displayZonesMap.get(layoutZone.id);

          // Determine what to render in this zone
          let zoneContent: React.ReactNode = null;

          if (useZoneBasedSystem && displayZone) {
            // NEW SYSTEM: Use displayZone configuration
            if (displayZone.displayZoneType === "content_blocks") {
              // Content zone - use playlist items
              const playListItems = displayZone.playList?.playListItems || [];
              const transitionType =
                scheduleData?.display?.blockTransitionAnimation ||
                displayZone.playList?.blockTransitionAnimation ||
                "none";

              zoneContent = (
                <ContentBlockArea
                  title={displayZone.displayZoneTitle}
                  playListItems={playListItems}
                  zoneId={layoutZone.id}
                  venue={scheduleData.venue}
                  themeConfig={themeConfig}
                  progressStyle={
                    displayZone.playList?.progressStyle as ProgressStyleValue
                  }
                  transitionType={
                    transitionType as BlockTransitionAnimationType
                  }
                />
              );
            } else if (displayZone.displayZoneType === "schedule") {
              // Schedule zone - filter events by displayZoneId
              zoneContent = (
                <Box
                  style={{
                    width: "100%",
                    height: "100%",
                    padding: "var(--mantine-spacing-md)",
                  }}
                >
                  <DisplayBlockSchedule
                    schedule={scheduleData}
                    events={scheduleData?.events || []}
                    displayZoneId={displayZone.displayZoneId}
                    displayZone={displayZone}
                    roomName={displayZone.room?.name || roomName || ""}
                    themeConfig={themeConfig}
                  />
                </Box>
              );
            }
          }

          return (
            <Box
              key={zoneKey}
              className={`main-section__zone main-section__zone--${
                displayZone?.displayZoneType || layoutZone.type
              }`}
              style={{
                width: `${layoutZone.widthPercent}%`,
                height: "100%",
                overflow: "hidden",
              }}
              data-zone-id={layoutZone.id}
              data-zone-type={displayZone?.displayZoneType || layoutZone.type}
              data-has-display-zone={!!displayZone}
              data-system={useZoneBasedSystem ? "zones" : "legacy"}
            >
              {zoneContent}
            </Box>
          );
        })}
      </Group>
    </Box>
  );
};
